/**
 * Phase 17.7 — Unified Campaign detail page (PARITY PLUG).
 *
 * Renders every conditional surface from the legacy detail pages
 * powered by the rewired unified service results:
 *
 *  - 6-card KPI strip (Mean Pk, Wilson 95 % CI, Successes, Failures,
 *    Mean CPA, Mean intercept time).
 *  - Failure-mode breakdown panel (9-class taxonomy).
 *  - CPA + intercept-time histograms.
 *  - Resolved-spec snapshot (33-variable distribution audit).
 *  - Per-sample table with drawn_values + clamp_count + trajectory link.
 *  - Sensitivity tornado (server PNG) + top-driver table + OAT response curves.
 *  - Target-Pk recommendation card + variable-diff table.
 *  - Envelope heatmap (server PNG with axes / colormap / resolution selectors).
 *  - Per-slice statistics table (Wilson CI + failure_mode_mix + centroid).
 *  - KMeans trajectory groups table.
 *  - GA convergence (2D + 3D) + best-individual gene table grouped by category.
 *  - GA recommended-config diff vs baseline.
 *  - Modulated-outcome (degradation) panel.
 *  - PDF + DOCX downloads.
 *
 * Conditional rendering: each section reads the corresponding key from
 * `campaign.results`. Unknown analysis goals collapse silently.
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, FileDown, Layers, Sliders, Target, BarChart3, Activity, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import unifiedCampaigns from "@/api/unifiedCampaigns";
// Phase 18 Ask 5 — detail-page panels for the new analytics fields.
import {
    BoTracePanel,
    AdaptivePolicyPanel,
    NarrowRlPanel,
    DecisionVariablesPanel,
} from "@/components/campaigns/Phase18ResultPanels";
// Pass H — calibration coverage card for procurement headline KPI.
import EngagementCalibrationCard from "@/components/sim/EngagementCalibrationCard";
// Pass I2 — fallback usage card (sibling of the calibration card).
import EngagementFallbackCard from "@/components/sim/EngagementFallbackCard";
// Pass J1 — manifest drift badge (rendered above calibration card).
import ManifestDriftBadge, { CampaignInFlightPill } from "@/components/sim/ManifestDriftBadge";
// Pass O1 — evidence-pack tile (4 PDF download links + reproducibility
// fingerprint footnote) below the calibration / fallback / drift stack.
// (Phase 19 follow-on) Legacy evidence tile import removed — see new
// Reports card below.

// Phase 19 WS-D3 — three cooperative-mode result components. Rendered
// only when ``isMultiAgentCampaign``; each component is null-safe so
// missing upstream payloads simply render nothing.
import MultiAgentTimeline from "@/components/phase19/MultiAgentTimeline";
import PerBatteryEffectivenessCard from "@/components/phase19/PerBatteryEffectivenessCard";
import FractionDefeatedWilsonSurface from "@/components/phase19/FractionDefeatedWilsonSurface";

const ConvergenceChart = lazy(() => import("@/components/optimisation/ConvergenceChart"));
const ConvergenceTrace3D = lazy(() => import("@/components/optimisation/ConvergenceTrace3D"));
// Phase 19 follow-on — campaign-mode Cesium battlespace viewer
// (lazy because Cesium is ~3 MB gzipped). Replaces the legacy
// Plotly-based PkTrajectory3D card outright; the multi-agent
// trajectory JSON drives static asset cylinders/cones + animated
// threat / interceptor polylines through Cesium's native Clock.
//
// Deterministic ``webpackChunkName`` so the vendor split-chunk
// pulled in by Cesium's ESM tree (``vendors-node_modules_cesium_…``)
// stays stable across rebuilds — without it, the auto-derived
// chunk filename shifts between dev compiles and a stale
// ``index.html`` ends up referencing a 404'd path.
const CampaignBattlespaceMap3D = lazy(
    () => import(
        /* webpackChunkName: "cesium-battlespace" */
        "@/components/phase19/CampaignBattlespaceMap3D"
    ),
);
// Second-perspective Plotly 3D flight-paths view — local-ENU
// projection of the same trajectory blob the Cesium scene reads.
const CampaignFlightPaths3D = lazy(
    () => import(
        /* webpackChunkName: "plotly-flight-paths" */
        "@/components/phase19/CampaignFlightPaths3D"
    ),
);
const FlightTelemetryTab = lazy(() => import("@/components/FlightTelemetryTab"));

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ------------------------------------------------------------------
// Shared helpers
// ------------------------------------------------------------------
function fmtPk(v, d = 3) {
    if (v == null || Number.isNaN(Number(v))) return "—";
    return Number(v).toFixed(d);
}
function fmtCi(lo, hi, d = 3) {
    if (lo == null || hi == null) return "—";
    return `[${Number(lo).toFixed(d)}, ${Number(hi).toFixed(d)}]`;
}
function authHeader() {
    const tok = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("uavsimnet_jwt")
        : null;
    return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// ------------------------------------------------------------------
// KPI strip — 6 cards
// ------------------------------------------------------------------
function KpiStrip({ campaign }) {
    const r = campaign?.results || {};
    const desc = r.descriptive_pk || {};
    const wilson = r.pk_wilson_ci || desc.wilson_ci || {};
    return (
        <Card data-testid="unified-detail-kpi-strip">
            <CardContent className="grid grid-cols-2 gap-4 p-6 md:grid-cols-3 lg:grid-cols-6">
                <div data-testid="kpi-mean-pk">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Mean Pk</div>
                    <div className="text-2xl font-semibold tabular-nums">{fmtPk(desc.mean_pk)}</div>
                </div>
                <div data-testid="kpi-wilson-ci">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Wilson 95 % CI</div>
                    <div className="text-base tabular-nums text-muted-foreground">
                        {fmtCi(wilson.lower, wilson.upper)}
                    </div>
                </div>
                <div data-testid="kpi-successes">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Successes</div>
                    <div className="text-2xl font-semibold tabular-nums">
                        {(desc.n_successes ?? 0)} / {desc.n_samples ?? "—"}
                    </div>
                </div>
                <div data-testid="kpi-failures">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Failures</div>
                    <div className="text-2xl font-semibold tabular-nums">
                        {(desc.n_failures ?? 0)} / {desc.n_samples ?? "—"}
                    </div>
                </div>
                <div data-testid="kpi-mean-cpa">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Mean CPA (m)</div>
                    <div className="text-2xl font-semibold tabular-nums">{fmtPk(desc.mean_cpa_m, 1)}</div>
                </div>
                <div data-testid="kpi-mean-intercept">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Mean intercept (s)</div>
                    <div className="text-2xl font-semibold tabular-nums">{fmtPk(desc.mean_intercept_time_s, 2)}</div>
                </div>
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Failure-mode breakdown panel (9-class taxonomy)
// ------------------------------------------------------------------
// ------------------------------------------------------------------
// Phase 19 WS-D4 — failure-mode breakdown extended with the two
// WS-B6 first-class taxonomy buckets (``magazine_exhausted`` +
// ``assignment_starvation``). The existing 9-class taxonomy still
// renders via the generic title-cased fallback for unknown keys;
// these two get explicit UK-spelt labels + defence-palette colours
// + striped/dashed treatments so the procurement reader can
// distinguish them at a glance from the prior 9 buckets.
// ------------------------------------------------------------------
const FAILURE_MODE_PROMOTED = {
    magazine_exhausted: {
        label: "Magazine exhausted",
        // Solid navy with diagonal stripes — CSS gradient delivers
        // the stripe pattern without an SVG primitive change.
        bg: "repeating-linear-gradient(45deg, #0B2545, #0B2545 4px, #1a3458 4px, #1a3458 8px)",
        testid: "failure-mode-promoted-magazine-exhausted",
    },
    assignment_starvation: {
        label: "Assignment starvation",
        // Steel-gray solid; the bar borders pick up the dashed
        // treatment via ``borderStyle: dashed``.
        bg: "#5A6B7C",
        testid: "failure-mode-promoted-assignment-starvation",
        dashed: true,
    },
};

function FailureModePanel({ counts }) {
    if (!counts || Object.keys(counts).length === 0) return null;
    const total = Object.values(counts).reduce((s, v) => s + Number(v || 0), 0) || 1;
    // Phase 19 WS-D4 — order the rows so the two promoted buckets
    // sit at the top of the breakdown if present, then the
    // remaining 9-class taxonomy in stable input order.
    const orderedKeys = [
        ...Object.keys(counts).filter((k) => k in FAILURE_MODE_PROMOTED),
        ...Object.keys(counts).filter((k) => !(k in FAILURE_MODE_PROMOTED)),
    ];
    return (
        <Card data-testid="unified-detail-failure-modes">
            <CardHeader className="flex flex-row items-center gap-2">
                <Activity className="size-4 text-muted-foreground" />
                <CardTitle className="text-base">Failure-mode breakdown</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-2">
                    {orderedKeys.map((k) => {
                        const v = counts[k];
                        const pct = (Number(v) / total) * 100;
                        const promoted = FAILURE_MODE_PROMOTED[k];
                        const label = promoted?.label || k;
                        const testid = promoted?.testid || `failure-mode-${k}`;
                        const bg = promoted?.bg || "#2563eb";
                        const dashed = Boolean(promoted?.dashed);
                        return (
                            <div
                                key={k}
                                data-testid={testid}
                                className="flex items-center gap-3"
                            >
                                <div className="w-44 truncate text-sm">{label}</div>
                                <div className="flex-1">
                                    <div className="h-3 w-full rounded bg-ux-surface-2/40">
                                        <div
                                            className={`h-3 rounded${dashed ? " border-2 border-dashed" : ""}`}
                                            style={{
                                                width: `${Math.max(2, pct)}%`,
                                                background: bg,
                                                borderColor: dashed ? "#0B2545" : undefined,
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="w-24 text-right text-sm tabular-nums">
                                    {v} <span className="text-muted-foreground">({pct.toFixed(0)}%)</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Inline histogram — pure SVG, no extra deps
// ------------------------------------------------------------------
function InlineHistogram({ values, title, xLabel, testId, color = "#2563eb" }) {
    if (!values || values.length === 0) return null;
    const arr = values.filter((v) => v != null && !Number.isNaN(Number(v))).map(Number);
    if (arr.length === 0) return null;
    const lo = Math.min(...arr), hi = Math.max(...arr);
    const n = Math.min(20, Math.max(4, Math.floor(arr.length / 3) + 1));
    const step = (hi - lo) / n || 1;
    const bins = new Array(n).fill(0);
    arr.forEach((v) => {
        const idx = Math.min(n - 1, Math.floor((v - lo) / step));
        bins[idx] += 1;
    });
    const maxC = Math.max(...bins) || 1;
    const W = 480, H = 140, padX = 30, padY = 20;
    return (
        <Card data-testid={testId}>
            <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
            <CardContent>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
                    {bins.map((c, i) => {
                        const w = (W - padX * 2) / n;
                        const h = (c / maxC) * (H - padY * 2);
                        return (
                            <rect
                                key={i}
                                x={padX + i * w + 1}
                                y={H - padY - h}
                                width={Math.max(2, w - 2)}
                                height={h}
                                fill={color}
                            />
                        );
                    })}
                    <line x1={padX} x2={W - padX} y1={H - padY} y2={H - padY} stroke="#94a3b8" strokeWidth="0.5" />
                    <text x={W / 2} y={H - 4} fontSize="10" fill="#64748b" textAnchor="middle">
                        {xLabel} [{lo.toFixed(1)} → {hi.toFixed(1)}]
                    </text>
                </svg>
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Sensitivity panel — top-drivers table + tornado PNG + OAT curves
// ------------------------------------------------------------------
function SensitivityPanel({ campaignId, sensitivity }) {
    const [tornadoUrl, setTornadoUrl] = useState(null);
    useEffect(() => {
        if (!sensitivity) return undefined;
        let revoke = null;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API_BASE}/api/campaigns/${campaignId}/tornado.png`, {
                    headers: { ...authHeader() },
                });
                if (!r.ok) return;
                const blob = await r.blob();
                if (cancelled) return;
                revoke = URL.createObjectURL(blob);
                setTornadoUrl(revoke);
            } catch {/* tolerate */}
        })();
        return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
    }, [campaignId, sensitivity]);
    if (!sensitivity) return null;
    const top = sensitivity.top_drivers || [];
    const oat = sensitivity.oat_curves || {};
    return (
        <Card data-testid="unified-detail-sensitivity-section">
            <CardHeader><CardTitle className="text-base">Sensitivity ranking ({sensitivity.method})</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div data-testid="sensitivity-detail-driver-tornado-3d-card" className="rounded border border-ux-border-1 bg-ux-surface-1 p-3">
                    {tornadoUrl ? (
                        <img src={tornadoUrl} alt="Sensitivity tornado" className="max-w-full"
                             data-testid="sensitivity-tornado-img" />
                    ) : (
                        <div className="text-sm text-muted-foreground">Loading tornado…</div>
                    )}
                </div>
                <Table data-testid="sensitivity-top-drivers-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Driver</TableHead>
                            <TableHead>Rank</TableHead>
                            <TableHead className="text-right">S1</TableHead>
                            <TableHead className="text-right">95 % CI</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {top.slice(0, 12).map((d) => (
                            <TableRow key={d.var_id} data-testid={`sensitivity-driver-${d.var_id}`}>
                                <TableCell className="font-mono text-xs">{d.var_id}</TableCell>
                                <TableCell>{d.rank}</TableCell>
                                <TableCell className="text-right tabular-nums">{fmtPk(d.index)}</TableCell>
                                <TableCell className="text-right tabular-nums text-muted-foreground">
                                    {fmtCi(d.ci_95_lo, d.ci_95_hi)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {Object.keys(oat).length > 0 && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                         data-testid="sensitivity-oat-grid">
                        {Object.entries(oat).slice(0, 6).map(([vid, c]) => (
                            <Card key={vid} data-testid={`oat-curve-${vid}`}>
                                <CardHeader><CardTitle className="text-sm">{vid}</CardTitle></CardHeader>
                                <CardContent>
                                    <OatMiniChart curve={c} />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
                {sensitivity.rank_stability && (
                    <div className="text-xs text-muted-foreground" data-testid="sensitivity-rank-stability">
                        Rank stability (top 5): first half [{(sensitivity.rank_stability.first_half || []).join(", ")}],
                        second half [{(sensitivity.rank_stability.second_half || []).join(", ")}],
                        overlap ratio {fmtPk(sensitivity.rank_stability.overlap_ratio, 2)}
                    </div>
                )}
                {sensitivity.failure_count != null && (
                    <div className="text-xs text-muted-foreground" data-testid="sensitivity-failure-rate">
                        Sample failure rate: {fmtPk(sensitivity.failure_rate, 3)}
                        ({sensitivity.failure_count} of {sensitivity.n_samples})
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
function OatMiniChart({ curve }) {
    if (!curve || !curve.sweep_values?.length) return <div className="text-xs text-muted-foreground">No data</div>;
    const sw = curve.sweep_values, pk = curve.pk_estimates, lo = curve.ci_lo, hi = curve.ci_hi;
    const W = 220, H = 90, padX = 10, padY = 10;
    const xMin = Math.min(...sw), xMax = Math.max(...sw);
    const X = (v) => padX + ((v - xMin) / (xMax - xMin || 1)) * (W - padX * 2);
    const Y = (v) => H - padY - (v) * (H - padY * 2);
    const pts = sw.map((x, i) => `${X(x)},${Y(pk[i])}`).join(" ");
    const band = sw.map((x, i) => `${X(x)},${Y(lo[i])}`).join(" ") + " " +
        [...sw].reverse().map((x, i) => {
            const idx = sw.length - 1 - i;
            return `${X(x)},${Y(hi[idx])}`;
        }).join(" ");
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
            <polygon points={band} fill="#2563eb" opacity="0.18" />
            <polyline points={pts} fill="none" stroke="#2563eb" strokeWidth="1.4" />
        </svg>
    );
}

// ------------------------------------------------------------------
// Target-Pk panel — recommendation card + variable-diff table
// ------------------------------------------------------------------
function TargetPkPanel({ tpk }) {
    if (!tpk) return null;
    const rdiff = tpk.recommended_config_diff || [];
    return (
        <Card data-testid="unified-detail-targetpk-section">
            <CardHeader>
                <CardTitle className="text-base">Target Pk knee — operator threshold</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="pk-target-recommendation-card">
                    <div data-testid="pk-target-recommendation-target">
                        <div className="text-xs uppercase text-muted-foreground">Target Pk</div>
                        <div className="text-xl font-semibold tabular-nums">
                            {fmtPk(tpk.target_threshold_used ?? tpk.target_pk)}
                        </div>
                    </div>
                    <div data-testid="pk-target-recommendation-achieved">
                        <div className="text-xs uppercase text-muted-foreground">Achieved Pk (mean)</div>
                        <div className="text-xl font-semibold tabular-nums">
                            {fmtPk(tpk.achieved_pk ?? tpk.mean_pk)}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase text-muted-foreground">Max range at Pk</div>
                        <div className="text-xl font-semibold tabular-nums">
                            {fmtPk(tpk.max_range_m_at_pk, 0)} m
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase text-muted-foreground">Feasibility</div>
                        <div>
                            {tpk.feasible
                                ? <Badge data-testid="pk-target-feasible-chip" className="bg-emerald-700/40">Feasible</Badge>
                                : <Badge data-testid="pk-target-infeasible-chip" variant="destructive">Infeasible</Badge>}
                        </div>
                    </div>
                </div>
                {rdiff.length > 0 && (
                    <Table data-testid="pk-target-diff-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Variable</TableHead>
                                <TableHead className="text-right">Nominal</TableHead>
                                <TableHead className="text-right">Recommended</TableHead>
                                <TableHead className="text-right">Δ</TableHead>
                                <TableHead className="text-right">[lo, hi]</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rdiff.slice(0, 15).map((r) => (
                                <TableRow key={r.var_id || r.id}
                                          data-testid={`pk-target-diff-row-${r.var_id || r.id}`}>
                                    <TableCell className="font-mono text-xs">{r.var_id || r.id}</TableCell>
                                    <TableCell className="text-right tabular-nums">{fmtPk(r.nominal)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{fmtPk(r.recommended)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{fmtPk(r.delta)}</TableCell>
                                    <TableCell className="text-right tabular-nums text-muted-foreground">
                                        [{fmtPk(r.lo)}, {fmtPk(r.hi)}] {r.unit || ""}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Geometry panel — heatmap PNG, slice table, KMeans groups
// ------------------------------------------------------------------
function GeometryPanel({ campaignId, heatmap, slices, groups }) {
    const [axesX, setAxesX] = useState("range_m");
    const [axesY, setAxesY] = useState("altitude_m");
    const [colormap, setColormap] = useState("viridis");
    const [resolution, setResolution] = useState("32x32");
    const [imgUrl, setImgUrl] = useState(null);
    useEffect(() => {
        if (!heatmap) return undefined;
        let cancelled = false; let revoke = null;
        (async () => {
            try {
                const params = new URLSearchParams({ axes_x: axesX, axes_y: axesY, colormap, resolution });
                const r = await fetch(
                    `${API_BASE}/api/campaigns/${campaignId}/heatmap.png?${params.toString()}`,
                    { headers: { ...authHeader() } },
                );
                if (!r.ok) return;
                const blob = await r.blob();
                if (cancelled) return;
                revoke = URL.createObjectURL(blob);
                setImgUrl(revoke);
            } catch {/* tolerate */}
        })();
        return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
    }, [campaignId, heatmap, axesX, axesY, colormap, resolution]);
    if (!heatmap && !slices?.length && !groups?.length) return null;
    return (
        <Card data-testid="unified-detail-geometry-section">
            <CardHeader>
                <CardTitle className="text-base">Envelope sweep — heatmap, slices, trajectory groups</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {heatmap && (
                    <>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <div>
                                <Label>Axes pair</Label>
                                <Select value={`${axesX}__${axesY}`} onValueChange={(v) => {
                                    const [x, y] = v.split("__"); setAxesX(x); setAxesY(y);
                                }}>
                                    <SelectTrigger data-testid="unified-heatmap-axes-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="range_m__altitude_m">range × altitude</SelectItem>
                                        <SelectItem value="range_m__bearing_deg">range × bearing</SelectItem>
                                        <SelectItem value="bearing_deg__altitude_m">bearing × altitude</SelectItem>
                                        <SelectItem value="range_m__approach_vector_deg">range × approach</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Colormap</Label>
                                <Select value={colormap} onValueChange={setColormap}>
                                    <SelectTrigger data-testid="unified-heatmap-colormap-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["viridis", "plasma", "magma", "cividis"].map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Resolution</Label>
                                <Select value={resolution} onValueChange={setResolution}>
                                    <SelectTrigger data-testid="unified-heatmap-resolution-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["16x16", "32x32", "64x64", "128x128"].map((r) => (
                                            <SelectItem key={r} value={r}>{r}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {imgUrl && <img src={imgUrl} alt="Envelope heatmap"
                                         className="max-w-2xl rounded border border-ux-border-1"
                                         data-testid="unified-heatmap-img" />}
                    </>
                )}
                {slices?.length > 0 && (
                    <div>
                        <h4 className="mb-2 text-sm font-semibold">Per-slice statistics</h4>
                        <Table data-testid="unified-detail-slice-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Slice</TableHead>
                                    <TableHead>N</TableHead>
                                    <TableHead className="text-right">Pk</TableHead>
                                    <TableHead className="text-right">95 % CI</TableHead>
                                    <TableHead>Centroid</TableHead>
                                    <TableHead>Mode mix</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {slices.slice(0, 30).map((sl) => (
                                    <TableRow key={sl.slice_id} data-testid={`slice-row-${sl.slice_id}`}>
                                        <TableCell>{sl.slice_id}</TableCell>
                                        <TableCell className="tabular-nums">{sl.n}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmtPk(sl.pk_mean)}</TableCell>
                                        <TableCell className="text-right tabular-nums text-muted-foreground">
                                            {fmtCi(sl.wilson_ci_low, sl.wilson_ci_high)}
                                        </TableCell>
                                        <TableCell className="text-xs tabular-nums text-muted-foreground">
                                            r={Math.round(sl.geometry_centroid?.range_m || 0)}m ·
                                            b={Math.round(sl.geometry_centroid?.bearing_deg || 0)}° ·
                                            alt={Math.round(sl.geometry_centroid?.altitude_m || 0)}m
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            {Object.entries(sl.failure_mode_mix || {}).slice(0, 3).map(
                                                ([k, v]) => `${k}:${v}`,
                                            ).join(", ")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {groups?.length > 0 && (
                    <div>
                        <h4 className="mb-2 text-sm font-semibold">Trajectory groups (KMeans)</h4>
                        <Table data-testid="unified-detail-kmeans-groups">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Group</TableHead>
                                    <TableHead>Members</TableHead>
                                    <TableHead>Dominant mode</TableHead>
                                    <TableHead className="text-right">Pk</TableHead>
                                    <TableHead>Centroid</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groups.map((g) => (
                                    <TableRow key={g.group_id} data-testid={`kmeans-group-${g.group_id}`}>
                                        <TableCell>#{g.group_id}</TableCell>
                                        <TableCell>{g.n_members}</TableCell>
                                        <TableCell className="capitalize">{g.dominant_failure_mode}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmtPk(g.pk_mean_in_group)}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            r={Math.round(g.geometry_centroid?.range_m || 0)}m ·
                                            b={Math.round(g.geometry_centroid?.bearing_deg || 0)}°
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// GA panel — convergence 2D/3D + best individual gene table + recommended diff
// ------------------------------------------------------------------
function GaPanel({ ga }) {
    // Phase 17.7 HOTFIX (Bug 1) — useMemo must run unconditionally
    // (react-hooks/rules-of-hooks). Read the gene table BEFORE the
    // early-return guard so the hook is called on every render.
    const geneTable = (ga?.best_individual?.gene_table) || [];
    const grouped = useMemo(() => {
        const out = {};
        for (const g of geneTable) {
            const cat = g.category || "guidance";
            if (!out[cat]) out[cat] = [];
            out[cat].push(g);
        }
        return out;
    }, [geneTable]);
    if (!ga) return null;
    const history = ga.population_history || [];
    const best = ga.best_individual || {};
    const rdiff = ga.recommended_config_diff || [];
    return (
        <Card data-testid="unified-detail-ga-section">
            <CardHeader><CardTitle className="text-base">GA optimisation</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <KvCard label="Best Pk" value={fmtPk(best.pk)} testId="ga-best-pk" />
                    <KvCard label="Baseline Pk" value={fmtPk(ga.baseline_pk)} testId="ga-baseline-pk" />
                    <KvCard label="Pk Δ" value={fmtPk((best.pk || 0) - (ga.baseline_pk || 0))} testId="ga-pk-delta" />
                    <KvCard label="Generations" value={String(ga.generations_completed)} testId="ga-generations" />
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Card data-testid="ga-convergence-2d-card">
                        <CardHeader><CardTitle className="text-sm">Convergence (2D)</CardTitle></CardHeader>
                        <CardContent>
                            {(ga.convergence_trace || []).length === 0 ? (
                                <div className="text-sm text-muted-foreground"
                                     data-testid="ga-convergence-2d-empty">
                                    No convergence trace yet — GA is still warming up or this
                                    campaign did not record per-generation data.
                                </div>
                            ) : (
                                <Suspense fallback={<div className="text-sm text-muted-foreground">Loading…</div>}>
                                    <ConvergenceChart trace={ga.convergence_trace || []} />
                                </Suspense>
                            )}
                        </CardContent>
                    </Card>
                    <Card data-testid="ga-convergence-3d-card">
                        <CardHeader><CardTitle className="text-sm">Convergence trace — 3D</CardTitle></CardHeader>
                        <CardContent>
                            {history.length === 0 ? (
                                <div className="text-sm text-muted-foreground"
                                     data-testid="ga-convergence-3d-empty">
                                    No population history yet — GA has not completed a generation.
                                </div>
                            ) : (
                                <Suspense fallback={<div className="text-sm text-muted-foreground">Loading…</div>}>
                                    <ConvergenceTrace3D history={history} />
                                </Suspense>
                            )}
                        </CardContent>
                    </Card>
                </div>
                {Object.keys(grouped).length > 0 && (
                    <div data-testid="ga-best-individual-gene-table">
                        <h4 className="mb-2 text-sm font-semibold">Best individual (gene table grouped by category)</h4>
                        {Object.entries(grouped).map(([cat, rows]) => (
                            <div key={cat} className="mb-3">
                                <div className="mb-1 text-xs uppercase text-muted-foreground">{cat}</div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Gene</TableHead>
                                            <TableHead className="text-right">Value</TableHead>
                                            <TableHead className="text-right">[lo, hi]</TableHead>
                                            <TableHead>Unit</TableHead>
                                            <TableHead className="text-right">Δ from default</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rows.map((r) => (
                                            <TableRow key={r.id} data-testid={`gene-row-${r.id}`}>
                                                <TableCell className="font-mono text-xs">{r.id}</TableCell>
                                                <TableCell className="text-right tabular-nums">{fmtPk(r.value)}</TableCell>
                                                <TableCell className="text-right tabular-nums text-muted-foreground">
                                                    [{fmtPk(r.lo)}, {fmtPk(r.hi)}]
                                                </TableCell>
                                                <TableCell className="text-xs text-muted-foreground">{r.unit}</TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {fmtPk(r.delta_from_default)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ))}
                    </div>
                )}
                {rdiff.length > 0 && (
                    <div data-testid="ga-recommended-config-diff">
                        <h4 className="mb-2 text-sm font-semibold">Recommended config diff vs baseline</h4>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Variable</TableHead>
                                    <TableHead className="text-right">Nominal</TableHead>
                                    <TableHead className="text-right">Recommended</TableHead>
                                    <TableHead className="text-right">Δ</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rdiff.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell className="font-mono text-xs">{r.id}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmtPk(r.nominal)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmtPk(r.recommended)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{fmtPk(r.delta)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {ga.early_stop_triggered && (
                    <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-200"
                         data-testid="ga-early-stop-banner">
                        Early-stop triggered — no improvement for the configured patience window.
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
function KvCard({ label, value, testId }) {
    return (
        <div className="rounded border border-ux-border-1 bg-ux-surface-1 p-3" data-testid={testId}>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold tabular-nums">{value}</div>
        </div>
    );
}

// ------------------------------------------------------------------
// Modulated-outcome (degradation) panel
// ------------------------------------------------------------------
function DegradationPanel({ mod, sha256 }) {
    if (!mod) return null;
    const acts = mod.activations || {};
    return (
        <Card data-testid="unified-detail-degradation-section">
            <CardHeader>
                <CardTitle className="text-base">Degradation pipeline (M2 modulation)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                <div className="text-sm">
                    Profile <strong>{mod.profile_id}</strong> v{mod.profile_version} — {mod.rule_count} rules.
                    IMU drift: {String(mod.imu_drift_enabled)}, radar dropout: {String(mod.radar_dropout_enabled)},
                    Markov: {String(mod.track_quality_markov_enabled)}.
                </div>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Rule</TableHead>
                            <TableHead className="text-right">Activations</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {Object.entries(acts).map(([k, v]) => (
                            <TableRow key={k} data-testid={`degradation-rule-${k}`}>
                                <TableCell className="font-mono text-xs">{k}</TableCell>
                                <TableCell className="text-right tabular-nums">{v}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {sha256 && sha256.length > 0 && (
                    <div className="text-xs text-muted-foreground" data-testid="degraded-track-sha256-audit">
                        Degraded-track SHA-256 audit: <code>{sha256.slice(0, 3).map((s) => s.slice(0, 12)).join(", ")}…</code>
                        ({sha256.length} entries)
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Per-sample table with trajectory link
// ------------------------------------------------------------------
function SamplesTable({ campaignId, samples }) {
    const list = samples || [];
    if (list.length === 0) return null;
    return (
        <Card data-testid="unified-detail-samples-section">
            <CardHeader>
                <CardTitle className="text-base">Per-sample table ({list.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Target IC</TableHead>
                            <TableHead className="text-right">Range (m)</TableHead>
                            <TableHead className="text-right">Pk</TableHead>
                            <TableHead className="text-right">CPA (m)</TableHead>
                            <TableHead>Failure mode</TableHead>
                            <TableHead className="text-right">Drawn vars</TableHead>
                            <TableHead className="text-right">Clamps</TableHead>
                            <TableHead>Trajectory</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {list.slice(0, 50).map((s) => (
                            <TableRow key={s.sample_idx} data-testid={`sample-row-${s.sample_idx}`}>
                                <TableCell>{s.sample_idx}</TableCell>
                                <TableCell className="text-xs tabular-nums">
                                    [{(s.target_position_t0 || []).map((v) => Math.round(v)).join(", ")}]
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{Math.round(s.range_at_launch_m || 0)}</TableCell>
                                <TableCell className="text-right tabular-nums">{fmtPk(s.pk)}</TableCell>
                                <TableCell className="text-right tabular-nums">{fmtPk(s.cpa_m, 1)}</TableCell>
                                <TableCell className="text-xs">{s.failure_mode || "—"}</TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {Object.keys(s.drawn_values || {}).length}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{s.clamp_count || 0}</TableCell>
                                <TableCell>
                                    {s.trajectory_path
                                        ? (<a
                                            href={`${API_BASE}/api/campaigns/${campaignId}/samples/${s.sample_idx}/trajectory`}
                                            target="_blank" rel="noreferrer"
                                            className="text-blue-400 underline"
                                            data-testid={`sample-traj-link-${s.sample_idx}`}>
                                            JSON
                                        </a>)
                                        : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Phase 17.8 — Live Progress Card (visible while status === 'running')
// Phase 17.9d — upgraded for in-flight visibility:
//   • stale-heartbeat banner (amber > 5 min, red > 30 min)
//   • failed-sample counter
//   • samples/min throughput from last 2 poll ticks
//   • current-sample preview block (idx, seed, ICs)
//   • watchdog CTA: when red threshold trips, render "Finalize at
//     current run?" prominently next to the Cancel button
//   • adaptive polling: caller backs off 4 s → 30 s after 3
//     unchanged poll ticks (handled in the page's tick() loop)
// ------------------------------------------------------------------
const STALE_AMBER_MS = 5 * 60 * 1000;
const STALE_RED_MS = 30 * 60 * 1000;

function _fmtEta(secs) {
    if (secs == null) return "—";
    if (secs < 60) return `${Math.round(secs)} s remaining`;
    if (secs < 3600) return `~${Math.round(secs / 60)} min remaining`;
    return `~${(secs / 3600).toFixed(1)} h remaining`;
}

function _fmtElapsed(secs) {
    if (secs == null) return "—";
    if (secs < 60) return `${Math.round(secs)} s`;
    if (secs < 3600) return `${Math.round(secs / 60)} min`;
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs - h * 3600) / 60);
    return `${h} h ${m} min`;
}

function _staleClass(deltaMs) {
    if (deltaMs == null) return null;
    if (deltaMs >= STALE_RED_MS) return "red";
    if (deltaMs >= STALE_AMBER_MS) return "amber";
    return null;
}

function ProgressCard({
    progress, status, lastHeartbeat, onCancel, onFinalize,
    throughputPerMin,
}) {
    if (!progress || (status !== "running" && status !== "queued")) return null;
    const total = Number(progress.samples_total ?? progress.total ?? 1) || 1;
    const completed = Number(progress.samples_completed ?? progress.completed ?? 0);
    const pct = Math.min(100, Math.max(0, Math.round((completed / total) * 100)));
    const eta = progress.eta_seconds != null ? Number(progress.eta_seconds) : null;
    const phase = progress.phase || progress.stage || "Initialising…";
    const elapsedS = progress.elapsed_s != null ? Number(progress.elapsed_s) : null;
    const failed = progress.failed_samples != null ? Number(progress.failed_samples) : null;
    const currentSample = progress.current_sample || null;

    // Stale-heartbeat detection. Prefer the explicit ``last_heartbeat``
    // field (Phase 17.9d); fall back to ``progress.last_heartbeat``.
    const hbRaw = lastHeartbeat || progress.last_heartbeat;
    const hbTs = hbRaw ? Date.parse(hbRaw) : null;
    const hbDelta = hbTs ? Date.now() - hbTs : null;
    const staleLevel = _staleClass(hbDelta);
    const hbAgeText = hbDelta == null
        ? "—"
        : hbDelta < 60_000 ? `${Math.round(hbDelta / 1000)} s ago`
        : hbDelta < 3_600_000 ? `${Math.round(hbDelta / 60_000)} min ago`
        : `${(hbDelta / 3_600_000).toFixed(1)} h ago`;

    const cardCls = staleLevel === "red"
        ? "border-red-500/60 bg-red-500/10"
        : staleLevel === "amber"
        ? "border-amber-500/60 bg-amber-500/10"
        : "border-sky-500/40 bg-sky-500/5";

    return (
        <Card data-testid="unified-detail-progress-card" className={cardCls}>
            <CardContent className="space-y-3 p-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            {status === "queued" ? "Queued" : "Running"}
                        </div>
                        <div className="text-base font-medium text-sky-200"
                             data-testid="progress-phase-label">
                            <Loader2 className="mr-1 inline-block size-3 animate-spin" />
                            {phase}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase text-muted-foreground">ETA</div>
                        <div className="text-sm tabular-nums"
                             data-testid="progress-eta">{_fmtEta(eta)}</div>
                    </div>
                </div>

                {staleLevel && (
                    <div className={`rounded-md border p-2 text-xs ${
                        staleLevel === "red"
                            ? "border-red-500/50 bg-red-500/10 text-red-200"
                            : "border-amber-500/50 bg-amber-500/10 text-amber-200"
                    }`} data-testid={`progress-stale-banner-${staleLevel}`}>
                        <div className="font-medium">
                            {staleLevel === "red"
                                ? `Worker stalled — no progress for ${hbAgeText}`
                                : `No progress for ${hbAgeText} — worker may be slow`}
                        </div>
                        {staleLevel === "red" && (
                            <div className="mt-1">
                                This campaign may have been killed by a deploy or
                                process restart. Finalize now to preserve any partial
                                data already written.
                            </div>
                        )}
                    </div>
                )}

                <Progress value={pct} data-testid="progress-bar" />

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span data-testid="progress-counters">
                        {progress.current_generation != null
                            ? `Generation ${progress.current_generation} / ${progress.total_generations ?? "?"}`
                            : `${completed} / ${total} samples`}
                        {progress.best_pk_so_far != null && (
                            <span data-testid="progress-best-pk-so-far"> · best Pk so far {Number(progress.best_pk_so_far).toFixed(3)}</span>
                        )}
                    </span>
                    <span>{pct}%</span>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-white/5 pt-2 text-[11px] text-muted-foreground sm:grid-cols-4">
                    <div>
                        <div className="uppercase tracking-wide">Elapsed</div>
                        <div className="tabular-nums text-foreground"
                             data-testid="progress-elapsed">{_fmtElapsed(elapsedS)}</div>
                    </div>
                    <div>
                        <div className="uppercase tracking-wide">Throughput</div>
                        <div className="tabular-nums text-foreground"
                             data-testid="progress-throughput">
                            {throughputPerMin != null
                                ? `${throughputPerMin.toFixed(1)} samples / min`
                                : "—"}
                        </div>
                    </div>
                    <div>
                        <div className="uppercase tracking-wide">Failed</div>
                        <div className="tabular-nums text-foreground"
                             data-testid="progress-failed-count">
                            {failed != null ? failed : "—"}
                        </div>
                    </div>
                    <div>
                        <div className="uppercase tracking-wide">Last heartbeat</div>
                        <div className="tabular-nums text-foreground"
                             data-testid="progress-heartbeat-age">{hbAgeText}</div>
                    </div>
                </div>

                {currentSample && (
                    <details className="rounded-md border border-white/5 bg-black/20 p-2 text-[11px]"
                             data-testid="progress-current-sample-details">
                        <summary className="cursor-pointer text-muted-foreground">
                            Current sample — idx {currentSample.idx ?? "?"}, seed {currentSample.seed ?? "?"}
                        </summary>
                        <div className="mt-2 space-y-1 font-mono text-muted-foreground">
                            {currentSample.target_position_t0 && (
                                <div>target_position_t0:&nbsp;
                                    [{currentSample.target_position_t0.join(", ")}]
                                </div>
                            )}
                            {currentSample.interceptor_position_t0 && (
                                <div>interceptor_position_t0:&nbsp;
                                    [{currentSample.interceptor_position_t0.join(", ")}]
                                </div>
                            )}
                            {currentSample.target_heading_deg != null && (
                                <div>target_heading_deg:&nbsp;
                                    {currentSample.target_heading_deg}°
                                </div>
                            )}
                        </div>
                    </details>
                )}

                <div className="flex justify-end gap-2">
                    {staleLevel === "red" && (
                        <Button variant="default" size="sm" onClick={onFinalize}
                                data-testid="unified-detail-finalize-cta-button"
                                className="bg-red-500/80 hover:bg-red-500">
                            Finalize at current run
                        </Button>
                    )}
                    {staleLevel !== "red" && completed > 0 && (
                        <Button variant="outline" size="sm" onClick={onFinalize}
                                data-testid="unified-detail-finalize-button">
                            Finalize now
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={onCancel}
                            data-testid="unified-detail-cancel-button">
                        <XCircle className="mr-1 size-3" /> Cancel
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------
export default function UnifiedCampaignDetailPage() {
    // Phase 17.7 HOTFIX (Bug 2) — App.js mounts the route as
    // `/campaigns/:campaignId`; rename the destructured key so the
    // page uses `id` internally without dropping the actual URL param.
    const { campaignId: id } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [error, setError] = useState(null);
    // Phase 17.9d — throughput sampler (rolling 2-tick window) and
    // adaptive-polling state. Refs so updates don't trigger re-renders.
    const throughputRef = useRef({ ts: null, completed: null, rate: null });
    const unchangedTicksRef = useRef(0);
    const [throughputPerMin, setThroughputPerMin] = useState(null);
    // Phase 19 demo follow-on — procurement-audit PDF generation state.
    const [procurementAuditState, setProcurementAuditState] = useState({
        pending: false, message: "", error: false,
    });
    // Phase 19 demo follow-on — lifted active-sample index so the
    // Cesium battlespace card and the Plotly flight-paths card stay
    // in lock-step. The Cesium card owns the selector UI; both cards
    // re-fetch their per-sample trajectory blob off this value.
    const [activeTrajectorySampleIdx, setActiveTrajectorySampleIdx] = useState(0);
    useEffect(() => {
        let cancelled = false;
        let pollHandle = null;
        let lastStatus = null;
        let lastCompleted = null;
        const tick = async () => {
            try {
                const c = await unifiedCampaigns.get(id);
                if (cancelled) return;
                // Toast on terminal status transition so operators see the
                // result without manual refresh (Phase 17.8 — live status).
                if (lastStatus && lastStatus !== c.status) {
                    if (c.status === "complete") toast.success("Campaign complete");
                    else if (c.status === "failed") toast.error(`Campaign failed — ${c.error || ""}`);
                    else if (c.status === "cancelled") toast.info(
                        c.cancelled_with_partial_results
                            ? "Campaign finalized with partial results"
                            : "Campaign cancelled",
                    );
                }
                lastStatus = c.status;
                setCampaign(c);

                // Phase 17.9d — throughput compute over the rolling
                // 2-tick window. Skips ticks where samples_completed
                // didn't change (avoids divide-by-zero rate).
                const completedNow = Number(
                    c?.progress?.samples_completed ?? c?.progress?.completed ?? 0,
                );
                const now = Date.now();
                if (
                    throughputRef.current.ts != null
                    && completedNow > throughputRef.current.completed
                ) {
                    const dtS = (now - throughputRef.current.ts) / 1000;
                    if (dtS > 0) {
                        const rate = ((completedNow - throughputRef.current.completed) / dtS) * 60;
                        throughputRef.current.rate = rate;
                        setThroughputPerMin(rate);
                    }
                }
                throughputRef.current.ts = now;
                throughputRef.current.completed = completedNow;

                // Phase 17.9d — adaptive polling. Stay at 4 s while
                // progress is moving; back off to 30 s after 3
                // consecutive ticks where samples_completed hasn't
                // changed (long-running or stuck campaign).
                if (lastCompleted === completedNow) {
                    unchangedTicksRef.current += 1;
                } else {
                    unchangedTicksRef.current = 0;
                }
                lastCompleted = completedNow;
                const nextDelay = (
                    c?.status === "running" || c?.status === "queued"
                ) ? (unchangedTicksRef.current >= 3 ? 30_000 : 4_000)
                  : null;
                if (nextDelay != null) {
                    pollHandle = setTimeout(tick, nextDelay);
                }
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load campaign");
            }
        };
        tick();
        return () => { cancelled = true; if (pollHandle) clearTimeout(pollHandle); };
    }, [id]);
    // Phase 17.8 HOTFIX — trajSamples useMemo MUST run on every render
    // (react-hooks/rules-of-hooks). Compute it unconditionally above the
    // error / loading early-returns; safe-navigate on a possibly-null
    // `campaign` so the empty case yields an empty array.
    const trajSamples = useMemo(() => (
        (campaign?.samples_summary || [])
            .filter((s) => !!s.trajectory_path)
            .slice(0, 20)
            .map((s) => ({
                sample_idx: s.sample_idx,
                success: !!s.success,
                failure_mode: s.failure_mode || "unknown",
            }))
    ), [campaign?.samples_summary]);
    if (error) return <div className="p-6 text-red-400">{error}</div>;
    if (!campaign) return <div className="p-6 text-muted-foreground">Loading…</div>;
    const r = campaign.results || {};
    // CP5.b — multi-agent (Phase 19) campaign detection from the
    // persisted CP4.5 fidelity fields on samples_summary. Avoids a
    // second fetch against the scenario doc: if any persisted sample
    // carries a Phase 19 fidelity field, the campaign ran the
    // multi-agent engine and the v2 report router will serve it.
    const isMultiAgentCampaign = (campaign.samples_summary || []).some(
        (s) => s && (s.pk_point != null || s.fraction_defeated != null),
    );
    const downloadPdf = async () => {
        try {
            await unifiedCampaigns.downloadPdf(id);
        } catch (e) {
            toast.error(`PDF download failed — ${e?.response?.status || ""} ${e?.message || ""}`);
        }
    };
    const downloadDocx = async () => {
        try {
            await unifiedCampaigns.downloadDocx(id);
        } catch (e) {
            toast.error(`DOCX download failed — ${e?.response?.status || ""} ${e?.message || ""}`);
        }
    };
    const downloadMultiagentPdf = async () => {
        try {
            await unifiedCampaigns.downloadMultiagentPdf(id);
        } catch (e) {
            toast.error(
                `Multi-agent PDF download failed — ${e?.response?.status || ""} ${e?.message || ""}`,
            );
        }
    };
    const downloadMultiagentDocx = async () => {
        try {
            await unifiedCampaigns.downloadMultiagentDocx(id);
        } catch (e) {
            toast.error(
                `Multi-agent DOCX download failed — ${e?.response?.status || ""} ${e?.message || ""}`,
            );
        }
    };
    // Phase 19 demo follow-on — WS-E POST procurement-audit endpoint.
    // Admin-only; persists the PDF, returns
    // {path, public_path, sha256, page_count, source_date_epoch,
    //  generated_at, cached}. Sidecar 5-min idempotency cache means
    // repeated clicks within the window return ``cached: true``.
    const generateProcurementAuditPdf = async () => {
        setProcurementAuditState({ pending: true, message: "Generating procurement audit PDF...", error: false });
        try {
            const r = await unifiedCampaigns.generateProcurementAuditPdf(id);
            const cachedNote = r.cached ? " (cached within 5-minute window)" : "";
            const mirror = r.public_path
                ? r.public_path.replace("/app/frontend/public", "")
                : null;
            setProcurementAuditState({
                pending: false,
                error: false,
                message: (
                    `Procurement audit PDF ready — ${r.page_count} page(s), `
                    + `sha256 ${String(r.sha256).slice(0, 12)}…${cachedNote}.`
                ),
            });
            if (mirror) {
                window.open(mirror, "_blank", "noopener,noreferrer");
            }
            toast.success("Procurement audit PDF generated.");
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "";
            setProcurementAuditState({
                pending: false, error: true,
                message: `Procurement audit PDF failed — ${detail}`,
            });
            toast.error(`Procurement audit failed — ${detail}`);
        }
    };
    const onCancel = async () => {
        try {
            const updated = await unifiedCampaigns.cancel(id);
            setCampaign(updated);
            toast.info("Campaign cancellation requested.");
        } catch (e) {
            toast.error(`Cancel failed — ${e?.message || ""}`);
        }
    };
    // Phase 17.9d — Finalize Now: confirm-gated operator call that
    // either fires the live worker's cancel-event exit (preserving
    // partial samples + analytics) or, for a dead worker, writes the
    // terminal state directly with finalize_reason=
    // operator_finalize_dead_worker.
    const onFinalize = async () => {
        if (!window.confirm(
            "Finalize this campaign at the current run?\n\n"
            + "Any samples already completed will be preserved if the worker "
            + "is still alive. If the worker has already died, no analytics "
            + "will be recoverable from the live process (but trajectory "
            + "files on disk remain available).",
        )) return;
        try {
            const updated = await unifiedCampaigns.finalize(id);
            setCampaign(updated);
            toast.success(
                updated?.cancelled_with_partial_results
                    ? "Campaign finalized — partial results preserved."
                    : "Campaign finalized.",
            );
        } catch (e) {
            const status = e?.response?.status;
            toast.error(
                status === 409 ? "Cannot finalize — campaign already terminal."
                : status === 410 ? "Cannot finalize — read-only campaign."
                : `Finalize failed — ${e?.message || ""}`,
            );
        }
    };
    // Phase 17.8 — adapter so the legacy PkTrajectory3D viewer reads our
    // unified per-sample trajectory JSON.
    //
    // Phase 19 follow-on — the legacy Plotly-based viewer has been
    // retired in favour of the Cesium ``CampaignBattlespaceMap3D``
    // component (rendered above the result panels). The unified
    // sample-trajectory endpoint is still consumed there directly via
    // ``unifiedCampaigns.sampleTrajectory``; no local adapter required.
    return (
        <div className="space-y-6 p-6" data-testid="unified-campaign-detail">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight" data-testid="campaign-title">
                        {campaign.name}
                    </h1>
                    {campaign.narrative_arc ? (
                        <p
                            data-testid="campaign-narrative-arc"
                            className="mt-1 italic"
                            style={{
                                color: "#5A6B7C",
                                fontSize: "12pt",
                                lineHeight: 1.35,
                            }}
                        >
                            {campaign.narrative_arc}
                        </p>
                    ) : null}
                    {(() => {
                        // Phase 19 WS-D2 — doctrine-mode pill. Render
                        // only when ``cooperative_mode`` is one of the
                        // three locked values; legacy campaigns omit
                        // the field and get no pill.
                        const mode = campaign.cooperative_mode;
                        if (mode !== "independent" && mode !== "cued" && mode !== "netted") {
                            return null;
                        }
                        const styleMap = {
                            independent: { bg: "#0B2545", fg: "#FFFFFF" },
                            cued:        { bg: "#5A6B7C", fg: "#FFFFFF" },
                            netted:      { bg: "#4FB6C6", fg: "#0B2545" },
                        };
                        const s = styleMap[mode];
                        return (
                            <span
                                data-testid={`doctrine-pill-${mode}`}
                                className="mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                                style={{ background: s.bg, color: s.fg }}
                            >
                                {mode}
                            </span>
                        );
                    })()}
                    <div className="text-sm text-muted-foreground">
                        Status: <strong>{campaign.status}</strong> · Scenario:{" "}
                        <Link to={`/plan/scenarios/${campaign.scenario_id}`} className="underline">
                            {campaign.scenario_name || campaign.scenario_id}
                        </Link>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={downloadPdf} data-testid="download-pdf-btn">
                        <Download className="mr-1 size-4" /> PDF
                    </Button>
                    <Button variant="outline" onClick={downloadDocx} data-testid="download-docx-btn">
                        <FileDown className="mr-1 size-4" /> DOCX
                    </Button>
                    {isMultiAgentCampaign && (
                        <>
                            <Button
                                variant="outline"
                                onClick={downloadMultiagentPdf}
                                disabled={campaign.status !== "complete"}
                                data-testid="download-multiagent-pdf-btn"
                                title="Generate the 5-band multi-agent SoW report from this campaign's persisted samples"
                            >
                                <Download className="mr-1 size-4" />
                                Generate multi-agent PDF
                            </Button>
                            <Button
                                variant="outline"
                                onClick={downloadMultiagentDocx}
                                disabled={campaign.status !== "complete"}
                                data-testid="download-multiagent-docx-btn"
                                title="Generate the 5-band multi-agent SoW DOCX from this campaign's persisted samples"
                            >
                                <FileDown className="mr-1 size-4" />
                                Generate multi-agent DOCX
                            </Button>
                        </>
                    )}
                </div>
            </div>
            {campaign.legacy && (
                <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-amber-200"
                     data-testid="legacy-banner">
                    LEGACY — read only. This campaign was migrated from the pre-Phase-17 schema.
                </div>
            )}
            <ProgressCard
                progress={campaign.progress}
                status={campaign.status}
                lastHeartbeat={campaign.last_heartbeat}
                throughputPerMin={throughputPerMin}
                onCancel={onCancel}
                onFinalize={onFinalize}
            />
            <KpiStrip campaign={campaign} />
            <FailureModePanel counts={r.failure_mode_counts} />
            {/* Phase 19 follow-on — full Cesium battlespace scene at
                the top of the campaign result section. Renders static
                assets (terrain imagery, radars, batteries, threat
                spawn markers) from the bound scenario doc, plus an
                animated CZML overlay (threat ingress + interceptor
                pursuits) sourced from the per-sample trajectory
                endpoint. Sample selector + show-trajectories toggle
                live inside the component. */}
            {campaign.scenario_id && (
                <Card data-testid="unified-detail-battlespace-3d-card">
                    <CardHeader>
                        <CardTitle className="text-base">
                            Battlespace 3D — static assets +
                            animated threat / interceptor tracks
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Suspense fallback={
                            <div className="text-sm text-muted-foreground">
                                Loading Cesium battlespace…
                            </div>
                        }>
                            <CampaignBattlespaceMap3D
                                campaignId={id}
                                scenarioId={campaign.scenario_id}
                                sampleIdx={0}
                                availableSamples={trajSamples}
                                activeSampleIdx={activeTrajectorySampleIdx}
                                onActiveSampleIdxChange={setActiveTrajectorySampleIdx}
                            />
                        </Suspense>
                    </CardContent>
                </Card>
            )}
            {/* Phase 19 follow-on — legacy PkTrajectory3D Plotly card
                retired. CampaignBattlespaceMap3D above now owns the
                multi-agent trajectory visualisation end-to-end. The
                Plotly card below is a SECOND perspective on the same
                trajectory blob (local ENU metres) — useful for
                inspecting altitude profiles and lateral separation
                that read poorly in the top-down Cesium view. */}
            {isMultiAgentCampaign && trajSamples.length > 0 && (
                <Card data-testid="unified-detail-flight-paths-3d-card">
                    <CardHeader>
                        <CardTitle className="text-base">
                            3D flight paths — local ENU perspective (post-run)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Suspense fallback={
                            <div className="text-sm text-muted-foreground">
                                Loading 3D flight-paths plot…
                            </div>
                        }>
                            <CampaignFlightPaths3D
                                campaignId={id}
                                sampleIdx={0}
                                activeSampleIdx={activeTrajectorySampleIdx}
                            />
                        </Suspense>
                    </CardContent>
                </Card>
            )}
            {/* Phase 17.9b — post-run flight telemetry section.
                Hidden when the campaign carries no persisted
                trajectories (the engine ran without per-sample
                trajectory persistence — common for GA-only
                optimisation campaigns). The component renders its
                own empty-state card otherwise. */}
            {trajSamples.length > 0 && (
                <Card data-testid="unified-detail-flight-telemetry-card">
                    <CardHeader>
                        <CardTitle className="text-base">
                            Flight telemetry — speed, climb, accel, altitude (post-run)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Suspense
                            fallback={
                                <div className="text-sm text-muted-foreground">
                                    Loading flight telemetry…
                                </div>
                            }
                        >
                            <FlightTelemetryTab
                                campaignId={id}
                                samples={campaign.samples_summary}
                            />
                        </Suspense>
                    </CardContent>
                </Card>
            )}
            {/* Pass J1 — manifest drift badge (renders only when drift detected) */}
            {/* Pass L1 — sibling in-flight hint pill (renders only when is_in_flight=true) */}
            <div>
                <ManifestDriftBadge drift={campaign.manifest_drift ?? null} />
                <CampaignInFlightPill drift={campaign.manifest_drift ?? null} />
            </div>
            {/* Pass H — calibration coverage headline KPI */}
            <EngagementCalibrationCard
                summary={campaign.engagement_calibration_summary ?? null}
            />
            {/* Pass I2 — fallback usage card (sibling card below) */}
            <EngagementFallbackCard
                summary={campaign.engagement_fallback_summary ?? null}
            />
            {/* Phase 19 demo follow-on — Reports section (replaces
                the legacy Evidence-pack tile). Three download surfaces:
                  • Procurement-audit PDF (WS-E POST, admin-only)
                  • Multi-agent PDF (existing GET streaming route)
                  • Multi-agent DOCX (existing GET streaming route)
                Navy header / steel-gray divider / teal action buttons
                per the defence palette. */}
            <Card data-testid="unified-detail-reports-card">
                <CardHeader>
                    <CardTitle className="text-base"
                               style={{ color: "#0B2545" }}>
                        Reports
                    </CardTitle>
                    <p className="text-sm" style={{ color: "#5A6B7C" }}>
                        Procurement-audit PDF (admin-only) + 5-band
                        multi-agent SoW reports for this campaign run.
                    </p>
                </CardHeader>
                <hr style={{ borderColor: "#5A6B7C", opacity: 0.35 }} />
                <CardContent className="flex flex-wrap gap-2 pt-3">
                    <Button
                        variant="default"
                        onClick={generateProcurementAuditPdf}
                        disabled={
                            campaign.status !== "complete"
                            || procurementAuditState.pending
                        }
                        data-testid="generate-procurement-audit-pdf-btn"
                        style={{
                            backgroundColor: "#4FB6C6",
                            color: "#0B2545",
                            borderColor: "#4FB6C6",
                        }}
                        title="POST /api/campaigns/{id}/procurement-audit-pdf"
                    >
                        <Download className="mr-1 size-4" />
                        {procurementAuditState.pending
                            ? "Generating..."
                            : "Generate procurement audit PDF"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={downloadMultiagentPdf}
                        disabled={campaign.status !== "complete"}
                        data-testid="download-multiagent-pdf-btn-reports"
                        title="GET /api/campaigns/{id}/report.multiagent.pdf"
                    >
                        <Download className="mr-1 size-4" />
                        Download multi-agent PDF
                    </Button>
                    <Button
                        variant="outline"
                        onClick={downloadMultiagentDocx}
                        disabled={campaign.status !== "complete"}
                        data-testid="download-multiagent-docx-btn-reports"
                        title="GET /api/campaigns/{id}/report.multiagent.docx"
                    >
                        <FileDown className="mr-1 size-4" />
                        Download multi-agent DOCX
                    </Button>
                    {procurementAuditState.message && (
                        <div className="w-full pt-2 text-sm"
                             data-testid="procurement-audit-status"
                             style={{
                                 color: procurementAuditState.error
                                     ? "#b91c1c"
                                     : "#0B2545",
                             }}>
                            {procurementAuditState.message}
                        </div>
                    )}
                </CardContent>
            </Card>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <InlineHistogram
                    title="CPA distribution"
                    xLabel="CPA (m)"
                    values={r.miss_distance_distribution || []}
                    testId="unified-detail-cpa-histogram"
                />
                <InlineHistogram
                    title="Intercept-time distribution"
                    xLabel="t_intercept (s)"
                    values={r.intercept_time_distribution || []}
                    testId="unified-detail-intercept-histogram"
                    color="#16a34a"
                />
            </div>
            <SensitivityPanel campaignId={id} sensitivity={r.sensitivity_ranking} />
            <TargetPkPanel tpk={r.target_pk_search} />
            <BoTracePanel tpk={r.target_pk_search} />
            <AdaptivePolicyPanel adp={r.adaptive_policy_training} />
            <NarrowRlPanel rl={r.narrow_rl_training} />
            <GeometryPanel
                campaignId={id}
                heatmap={r.envelope_heatmap}
                slices={r.slice_stats}
                groups={r.kmeans_groups}
            />
            <GaPanel ga={r.ga_optimisation_convergence} />
            <DegradationPanel
                mod={r.modulated_outcome_summary}
                sha256={r.degraded_track_sha256}
            />
            <DecisionVariablesPanel dv={r.decision_variables} />
            {/* Phase 19 WS-D3 — cooperative-mode result panels. Render
                only when ``isMultiAgentCampaign`` (existing
                derivation): per-battery effectiveness card on the
                left, Wilson-CI surface on the right, multi-agent
                timeline below. Each component returns null on a
                missing-shape payload so they're safe inside the gate. */}
            {isMultiAgentCampaign && (
                <div
                    data-testid="phase19-cooperative-result-panels"
                    className="space-y-4"
                >
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <PerBatteryEffectivenessCard
                            perBattery={r.per_battery_effectiveness}
                        />
                        <FractionDefeatedWilsonSurface
                            fractionDefeated={r.fraction_defeated}
                        />
                    </div>
                    <MultiAgentTimeline
                        timeline={r.multi_agent_timeline}
                    />
                </div>
            )}
            <SamplesTable campaignId={id} samples={campaign.samples_summary} />
        </div>
    );
}
